import { useSyncExternalStore } from 'react';
import { SECTORS, type SectorId } from '@cradle7/shared';
import { getUserSettings, setUserSettings, subscribeUserSettings } from './userSettings';

const listeners = new Set<() => void>();
function emit(): void { listeners.forEach((fn) => fn()); }

// Bridge userSettings changes → our listeners so React via useSyncExternalStore
// updates whenever unlockedSectors/currentSectorId change from any caller.
subscribeUserSettings(() => emit());

export function getCurrentSectorId(): SectorId {
  return getUserSettings().currentSectorId;
}

export function setCurrentSectorId(id: SectorId): void {
  setUserSettings({ currentSectorId: id });
}

export function getUnlockedSectors(): Set<SectorId> {
  return new Set(getUserSettings().unlockedSectors);
}

export function isUnlocked(id: SectorId): boolean {
  return getUnlockedSectors().has(id);
}

export function unlockSector(id: SectorId): void {
  const current = getUnlockedSectors();
  if (current.has(id)) return;
  current.add(id);
  setUserSettings({ unlockedSectors: Array.from(current) });
}

/**
 * Cascade-unlock: find sectors gated by `winnerId` and unlock them. Returns the
 * first newly-unlocked id (for fireAchievement feedback), or null if none.
 */
export function unlockNextOf(winnerId: SectorId): SectorId | null {
  const gated = SECTORS.filter((s) => s.requiresWin === winnerId);
  let firstNewlyUnlocked: SectorId | null = null;
  const current = getUnlockedSectors();
  for (const s of gated) {
    if (!current.has(s.id)) {
      if (firstNewlyUnlocked === null) firstNewlyUnlocked = s.id;
      current.add(s.id);
    }
  }
  if (firstNewlyUnlocked) {
    setUserSettings({ unlockedSectors: Array.from(current) });
  }
  return firstNewlyUnlocked;
}

export function subscribeSectors(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

// Cached snapshot to satisfy useSyncExternalStore's requirement that the
// snapshot object is stable (same reference) between emissions.
let _snapshot: { current: SectorId; unlocked: Set<SectorId> } | null = null;
function buildSnapshot(): { current: SectorId; unlocked: Set<SectorId> } {
  const s = getUserSettings();
  const newUnlocked = new Set<SectorId>(s.unlockedSectors as SectorId[]);
  // Only allocate a new object when the data actually changed.
  if (
    _snapshot !== null &&
    _snapshot.current === s.currentSectorId &&
    _snapshot.unlocked.size === newUnlocked.size &&
    s.unlockedSectors.every((id) => _snapshot!.unlocked.has(id))
  ) {
    return _snapshot;
  }
  _snapshot = { current: s.currentSectorId, unlocked: newUnlocked };
  return _snapshot;
}

/** Hook for components that need to re-render on sector state changes. */
export function useSectorState(): { current: SectorId; unlocked: Set<SectorId> } {
  return useSyncExternalStore(subscribeSectors, buildSnapshot, buildSnapshot);
}
